import * as XLSX from "xlsx";

const TINTER_IMPORT_HEADERS = {
  color_code: ["tinter code"],
  color_name: ["tinter name"],
  color_name_th: ["thai name"],
  density: ["density"],
};

const normalizeHeader = (value) => {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
};

const toTrimmedString = (value) => {
  return String(value ?? "").trim();
};

const buildSkippedRow = ({ rowNumber, sheetName, reason }) => {
  return { rowNumber, sheetName, reason };
};

const getHeaderIndexes = (headerRow = []) => {
  const normalizedHeaders = headerRow.map(normalizeHeader);

  return Object.entries(TINTER_IMPORT_HEADERS).reduce(
    (indexes, [field, aliases]) => {
      const headerIndex = normalizedHeaders.findIndex((header) =>
        aliases.includes(header)
      );

      return {
        ...indexes,
        [field]: headerIndex,
      };
    },
    {}
  );
};

const hasAllRequiredHeaders = (headerIndexes) => {
  return Object.values(headerIndexes).every((index) => index > -1);
};

const isBlankImportRow = (row = [], headerIndexes) => {
  return Object.values(headerIndexes).every((index) => {
    return !toTrimmedString(row[index]);
  });
};

// หลักการทำงาน: อ่าน metadata ของ workbook แล้วคืนเฉพาะชีตที่ Excel ตั้งเป็น visible
// เพื่อให้ import ไม่ดึงข้อมูลจากชีต hidden หรือ veryHidden ที่ผู้ใช้ไม่ได้เห็นในไฟล์
export const getVisibleSheetNames = (workbook = {}) => {
  const sheetStates = new Map(
    (workbook.Workbook?.Sheets || []).map((sheet) => [
      sheet.name,
      Number(sheet.Hidden || 0),
    ])
  );

  return (workbook.SheetNames || []).filter((sheetName) => {
    return Number(sheetStates.get(sheetName) || 0) === 0;
  });
};

// หลักการทำงาน: แปลงแถวจากชีตที่มองเห็นให้เป็น payload ของ master tinter
// โดยยึด header ที่ระบบรองรับ และข้ามเฉพาะแถวว่าง/แถวที่ field จำเป็นไม่ครบหรือ density ไม่ใช่ตัวเลข
export const normalizeTinterRows = (rows = [], sheetName = "") => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      colors: [],
      skippedRows: [],
      errors: [`${sheetName || "Sheet"}: missing header row`],
    };
  }

  const headerIndexes = getHeaderIndexes(rows[0]);

  if (!hasAllRequiredHeaders(headerIndexes)) {
    return {
      colors: [],
      skippedRows: [],
      errors: [`${sheetName || "Sheet"}: missing required headers`],
    };
  }

  const colors = [];
  const skippedRows = [];

  rows.slice(1).forEach((row, index) => {
    const rowNumber = index + 2;

    if (isBlankImportRow(row, headerIndexes)) {
      return;
    }

    const color_code = toTrimmedString(row[headerIndexes.color_code]);
    const color_name = toTrimmedString(row[headerIndexes.color_name]);
    const color_name_th = toTrimmedString(row[headerIndexes.color_name_th]);
    const rawDensity = row[headerIndexes.density];
    const hasDensity = toTrimmedString(rawDensity);

    if (!color_code || !color_name || !color_name_th || !hasDensity) {
      skippedRows.push(
        buildSkippedRow({
          rowNumber,
          sheetName,
          reason: "missing_required_field",
        })
      );
      return;
    }

    const density = Number(rawDensity);

    if (!Number.isFinite(density)) {
      skippedRows.push(
        buildSkippedRow({
          rowNumber,
          sheetName,
          reason: "invalid_density",
        })
      );
      return;
    }

    colors.push({
      color_code,
      color_name,
      color_name_th,
      density,
    });
  });

  return {
    colors,
    skippedRows,
    errors: [],
  };
};

// หลักการทำงาน: รับไฟล์ Excel จาก browser, เปิด workbook ด้วย SheetJS แล้วรวมเฉพาะข้อมูลจากชีตที่ visible
// ผลลัพธ์แยก valid rows, skipped rows และ errors เพื่อให้ UI ถามยืนยันก่อนส่งเข้า backend
export const parseVisibleTinterWorkbook = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const visibleSheetNames = getVisibleSheetNames(workbook);

  if (visibleSheetNames.length === 0) {
    return {
      colors: [],
      skippedRows: [],
      errors: ["No visible sheets found"],
      visibleSheetNames,
    };
  }

  const sheetResults = visibleSheetNames.map((sheetName) => {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      raw: true,
      defval: "",
      blankrows: false,
    });

    return normalizeTinterRows(rows, sheetName);
  });

  return {
    colors: sheetResults.flatMap((result) => result.colors),
    skippedRows: sheetResults.flatMap((result) => result.skippedRows),
    errors: sheetResults.flatMap((result) => result.errors),
    visibleSheetNames,
  };
};
